import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { z } from 'zod';
import SiafoLogo from '@/components/layout/SiafoLogo';
import LoginBackground from '@/components/auth/LoginBackground';
import { debugLog } from '@/lib/debugLogger';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';

const loginSchema = z.object({
  email: z.string().email('Email inválido').max(255),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
});

/**
 * AuthPage — Login form ONLY.
 * 
 * ARCHITECTURAL RULE: This component has ZERO navigation logic.
 * It only renders the form and calls signIn().
 * Redirection is handled exclusively by PublicRoute (in App.tsx)
 * and ProtectedRoute.
 */
export default function AuthPage() {
  const navigate = useNavigate();
  const { loading: authLoading, signIn } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      const validation = loginSchema.safeParse({
        email: loginEmail,
        password: loginPassword,
      });
      
      if (!validation.success) {
        setError(validation.error.errors[0].message);
        setIsLoading(false);
        return;
      }
      
      debugLog('auth', '[NetUno:AuthFlow] AuthPage::signIn:start');

      const { error: signInError } = await signIn(loginEmail, loginPassword);
      
      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          setError('Email ou senha incorretos');
        } else if (signInError.message.includes('Email not confirmed')) {
          setError('Email não confirmado. Verifique sua caixa de entrada.');
        } else {
          setError(signInError.message);
        }
        setIsLoading(false);
      } else {
        // Login succeeded — keep isLoading=true ("Entrando...")
        // PublicRoute will detect authenticated state and redirect.
        debugLog('auth', '[NetUno:AuthFlow] AuthPage::signIn:success — aguardando redirect via PublicRoute');
      }
    } catch (err) {
      setError('Erro ao fazer login. Tente novamente.');
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4 relative overflow-hidden">
      <LoginBackground />

      <Card className="w-full max-w-md shadow-none border-none bg-transparent relative z-10">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <SiafoLogo size="lg" showText={true} showSubtitle={true} />
          </div>
          <CardDescription>Acesse sua conta para continuar</CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="seu@email.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="login-password">Senha</Label>
              <Input
                id="login-password"
                type="password"
                placeholder="••••••"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>
          
          <p className="text-center text-sm text-muted-foreground mt-6">
            O acesso é controlado. Contate um administrador para obter credenciais.
          </p>

          <p className="text-center text-xs text-muted-foreground mt-3">
            Quer cadastrar sua empresa?{' '}
            <button
              type="button"
              onClick={() => navigate(buildPathWithPreviewToken('/onboarding'))}
              className="underline hover:text-foreground transition-colors"
            >
              Criar conta
            </button>
          </p>
        </CardContent>
      </Card>

      {/* Rodapé */}
      <p className="absolute bottom-4 text-xs text-muted-foreground/70 text-center">
        © 2026 NetUno. Todos os direitos reservados.
      </p>
    </div>
  );
}
